import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component-with-filter';
import InternalHeader from '../InternalHeader';
import { FaCog, FaUpload } from 'react-icons/fa';
import Sidebar from '../Sidebar';
import CreateInternalUserModal from './CreateInternalUserModal';
import './InternalUser.css';
import CustomPagination from '../CustomPagination/CustomPagination';
import FilterUsers from '../Filter/FilterUsers';
import AccessControl from '../AccessControl';

const PortalInternalUsers = () => {
	const [internalUsers, setInternalUsers] = useState([]);
	const [filteredInternalUsers, setFilteredInternalUsers] = useState([]);
	const [isSidebarVisible, setIsSidebarVisible] = useState(true);
	const [activeContent, setActiveContent] = useState('default');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [searchText, setSearchText] = useState('');
	const totalRecords = internalUsers.length;
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [viewOption, setViewOption] = useState('all');
	const [successModalVisible, setSuccessModal] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState('');
	const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
	const [logs, setLogs] = useState([]);

	const logChange = (action, details) => {
		setLogs(prevLogs => [...prevLogs, { timestamp: new Date().toLocaleString(), action, details }]);
	};

	const handleActionsToggle = () => {
		setIsActionsDropdownOpen(!isActionsDropdownOpen);
	};

	useEffect(() => {
		const fetchInternalUsers = async () => {
			setLoading(true);
			try {
				const response = await fetch('https://api.hgcdental.clinic/api/internalusers');
				if (!response.ok) {
					throw new Error('Network response was not ok');
				}
				const data = await response.json();
				setInternalUsers(data);
				setFilteredInternalUsers(data);
				logChange('Fetched', `Loaded ${data.length} internal users.`);
			} catch (error) {
				console.error('Error fetching internal users:', error);
				setError('Failed to load internal users. Please try again later.');
			} finally {
				setLoading(false);
			}
		};

		fetchInternalUsers();
	}, []);

	useEffect(() => {
		const results = internalUsers.filter(user =>
			`${user.firstName} ${user.lastName}`.toLowerCase().includes(searchText.toLowerCase()) ||
			user.email.toLowerCase().includes(searchText.toLowerCase())
		);
		setFilteredInternalUsers(results);
	}, [searchText, internalUsers]);

	const toggleSidebar = () => {
		setIsSidebarVisible(!isSidebarVisible);
	};

	const handleUserCreated = (newUser) => {
		setInternalUsers((prevUsers) => [...prevUsers, newUser]);
		setFilteredInternalUsers((prevUsers) => [...prevUsers, newUser]);
		setSuccessModal(true);
	};

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	const handleExport = () => {
		if (!Array.isArray(selectedUsers) || selectedUsers.length === 0) {
			alert('No users selected for export!');
			return;
		}

		// Define headers with Full Name instead of separate first/last names
		const csvRows = [
			['Full Name', 'Email', 'Contact Number'],
			...selectedUsers.map(user => [
				`${user.firstName} ${user.lastName}`,
				user.email,
				`\t${user.contactNumber}`,
			]),
		];

		const csvContent = csvRows.map(row => row.join(',')).join('\n');

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);

		// Trigger download
		const link = document.createElement('a');
		link.href = url;
		link.download = 'ExternalUsers.csv';
		link.click();
		URL.revokeObjectURL(url);
	};

	const handleRowSelected = ({ selectedRows }) => {
		setSelectedUsers(selectedRows);
	};

	const handleRowsPerPageChange = (newRowsPerPage) => {
		setRowsPerPage(Number(newRowsPerPage));
		setCurrentPage(1);
	};

	const handleOverlayClick = (e) => {
		if (e.target.className === 'modal-overlay') {
			this.closeModal();
		}
	};

	const closeSuccessModal = () => {
		setSuccessModal(false);
	};

	const columns = [
		{
			name: 'Name',
			selector: row => `${row.firstName} ${row.lastName}`,
			sortable: true,
			filterable: true
		},
		{
			name: 'Email',
			selector: row => row.email,
			sortable: true,
			filterable: true
		},
	];

	const customStyles = {
		rows: {
			style: {
				borderBottom: '1px solid #ddd',
			},
		},
		headCells: {
			style: {
				backgroundColor: '#007bff',
				color: 'white',
				fontWeight: 'bold',
				padding: '15px 10px',
				textAlign: 'center',
			},
		},
		cells: {
			style: {
				padding: '12px 10px',
				borderRight: '1px solid #ddd',
			},
		},
		pagination: {
			style: {
				display: 'flex',
				justifyContent: 'center',
				padding: '10px',
				backgroundColor: '#f4f4f4',
			},
		},
		sortIcon: {
			style: {
				color: '#007bff',
				fontSize: '18px',
			},
		},
	};

	const paginatedData = filteredInternalUsers.slice(
		(currentPage - 1) * rowsPerPage,
		currentPage * rowsPerPage
	);

	const totalPages = Math.ceil(filteredInternalUsers.length / rowsPerPage);

	return (
		<AccessControl>
			<div className={`data-table-page ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
				<InternalHeader moduleName="Internal Users" toggleSidebar={toggleSidebar} />
				<div className="data-table-container">
					<Sidebar
						isSidebarVisible={isSidebarVisible}
						setActiveContent={setActiveContent}
					/>
					<div className="data-table-container2">
						<div className="data-table-header">
							<div className="flex-div">
								<button className="create-button" onClick={() => setIsModalOpen(true)}>New</button>
							</div>
							<div>
								{selectedUsers.length > 0 ? (
									<div className="actions-dropdown-container">
										<button onClick={handleActionsToggle} className="actions-button">
											<FaCog />  Actions
										</button>
										{isActionsDropdownOpen && (
											<div className="actions-dropdown-options1">
												<div onClick={handleExport}>
													<FaUpload /> Export Records
												</div>
											</div>
										)}
									</div>
								) : (
									<FilterUsers
										searchText={searchText}
										setSearchText={setSearchText}
										viewOption={viewOption}
										setViewOption={setViewOption}
									/>
								)}
							</div>
							<div className="column-div">
								<CustomPagination
									currentPage={currentPage}
									totalPages={totalPages}
									rowsPerPage={rowsPerPage}
									onPageChange={handlePageChange}
									onRowsPerPageChange={handleRowsPerPageChange}
									totalRecords={totalRecords}
								/>
							</div>
						</div>

						{loading ? (
							<div>Loading internal users...</div>
						) : error ? (
							<div className="error-message">{error}</div>
						) : (
							<div className="table-form">
								<div>
									<DataTable
										title="Internal Users"
										columns={columns}
										data={filteredInternalUsers}
										highlightOnHover
										striped
										selectableRows
										selectableRowsHighlight
										selectableRowsVisibleOnly
										noDataComponent="No internal users found."
										customStyles={customStyles}
										onSelectedRowsChange={handleRowSelected}
									/>
								</div>
							</div>
						)}

						<div className="log-display-section">
							<h2>Logs</h2>
							<div className="log-container">
								<ul>
									{logs.map((log, index) => (
										<li key={index}>{log.timestamp} - {log.action}: {log.details}</li>
									))}
								</ul>
							</div>
						</div>

						<CreateInternalUserModal
							isOpen={isModalOpen}
							onRequestClose={() => setIsModalOpen(false)}
							onUserCreated={handleUserCreated}
						/>


						{successModalVisible && (
							<div className="modal-overlay" onClick={handleOverlayClick}>
								<div className="modal success-modal" onClick={(e) => e.stopPropagation()}>
									<h3>Sucess!</h3>
									<div class="modal-divider" />
									<p>Internal User created successfully.</p>
									<div class="modal-divider" />
									<button className="confirm-button" onClick={closeSuccessModal}>Close</button>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</AccessControl>
	);
};

export default PortalInternalUsers;
